import React, { ReactElement, useEffect, useRef } from 'react'

// Cache to store fetched SVGs
const svgCache: { [key: string]: string } = {}
// Map to store ongoing fetch promises
const fetchPromises: { [key: string]: Promise<void> } = {}

export function AwesomeIconDynamicClient(props: { icon: string }): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    const src = `/fontawesome/svgs/sharp-light/${props.icon}.svg`

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const requestAlreadyInprogress = !!fetchPromises[src]

    if (svgCache[src]) {
      // If the SVG is already cached, use it
      loadSVGFromCache(svgCache[src], containerRef.current, 'mr-2 h-4 w-4 text-primary')
    } else if (requestAlreadyInprogress) {
      // If a fetch is already in progress, wait for it to complete
      void fetchPromises[src].then(() => {
        loadSVGFromCache(svgCache[src], containerRef.current!, 'mr-2 h-4 w-4 text-primary')
      })
    } else {
      // Start a new fetch and cache the promise
      fetchPromises[src] = loadAndModifySVG(src, containerRef.current, 'mr-2 h-4 w-4 text-primary')
    }
  }, [props.icon])

  return <div ref={containerRef} />
}

function loadSVGFromCache(svgText: string, container: HTMLDivElement, className: string) {
  if (!container || container.hasChildNodes()) return

  const tempContainer = document.createElement('div')
  tempContainer.innerHTML = svgText
  const svgElement = tempContainer.querySelector('svg')!
  svgElement.setAttribute('class', className)
  svgElement.setAttribute('aria-hidden', 'true')
  svgElement.setAttribute('focusable', 'false')
  svgElement.setAttribute('role', 'img')
  svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg')

  // Get the SVG element
  const paths = svgElement.querySelectorAll('path')!

  // Change the fill color
  paths.forEach((svgElement) => svgElement.setAttribute('fill', 'currentColor'))

  // Inject the modified SVG into the DOM
  container.appendChild(svgElement)
}

async function loadAndModifySVG(src: string, container: HTMLDivElement, className: string) {
  const response = await fetch(src)
  const svgText = await response.text()

  // Cache the SVG text
  svgCache[src] = svgText

  // Remove the fetch promise from the map
  delete fetchPromises[src]

  // Proceed with loading the SVG from the cached text
  loadSVGFromCache(svgText, container, className)
}
